import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import PageHero from '../components/ui/PageHero';
import FadeUp from '../components/ui/FadeUp';
import SectionHeader from '../components/ui/SectionHeader';
import { PartsSearchForm } from '../components/ui/PartsSearchForm';
import { CATEGORIES, BUSINESS } from '../data/siteData';

const FEATURED = [
  { icon: '🔧', part: 'Engine Assembly', vehicle: '2019 Toyota Camry 2.5L', condition: 'Good', price: '$850', tag: 'Popular' },
  { icon: '⚙️', part: 'Automatic Transmission', vehicle: '2017 Ford F-150 6-Speed', condition: 'Tested', price: '$1,200', tag: 'Available' },
  { icon: '🚗', part: 'Front Bumper Assembly', vehicle: '2020 Honda Civic', condition: 'Excellent', price: '$180', tag: 'New In' },
  { icon: '⚡', part: 'Alternator 160A', vehicle: '2016 Chevrolet Silverado', condition: 'Good', price: '$95', tag: 'Available' },
  { icon: '🛞', part: 'Alloy Wheel Set (4)', vehicle: '17" — Universal Fit', condition: 'Good', price: '$320', tag: 'Popular' },
  { icon: '❄️', part: 'Radiator', vehicle: '2018 Jeep Grand Cherokee', condition: 'Tested', price: '$140', tag: 'Available' },
];

const CONDITION_COLOR = { 'Excellent': '#1a8a5a', 'Good': '#2563eb', 'Tested': 'var(--orange)' };

export default function InventoryPage() {
  const [searchParams] = useSearchParams();
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'All');

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  return (
    <>
      <PageHero
        tag="Parts Inventory"
        title="Search Our Inventory"
        subtitle="500+ vehicles on-site. Search by year, make, model or part name. Results powered by CarParts.com."
      />

      {/* Main Search */}
      <section style={{ background: 'var(--ice)', padding: '3rem 2rem' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '2.5rem', boxShadow: '0 4px 40px rgba(13,27,42,0.08)' }}>
            <h2 style={{ fontFamily: '"Bebas Neue"', fontSize: '1.6rem', color: 'var(--navy)', letterSpacing: '0.04em', marginBottom: '1.5rem' }}>
              🔍 Find Your Part
            </h2>
            <PartsSearchForm />
            <p style={{ marginTop: '1rem', fontSize: '0.82rem', color: 'var(--text-light)', textAlign: 'center' }}>
              Part not showing? <Link to="/request-part" style={{ color: 'var(--orange)', textDecoration: 'none' }}>Submit a part request</Link> and we'll locate it across our network.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section style={{ padding: '3rem 2rem 1.5rem', background: 'white' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <FadeUp>
            <SectionHeader tag="Browse By Category" title="What Are You Looking For?" />
          </FadeUp>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1.5rem' }}>
            <CategoryPill label="All" active={activeCategory === 'All'} onClick={() => setActiveCategory('All')} />
            {CATEGORIES.map(cat => (
              <CategoryPill key={cat.name} label={`${cat.icon} ${cat.name}`} active={activeCategory === cat.search} onClick={() => setActiveCategory(cat.search)} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Parts */}
      <section style={{ padding: '2rem 2rem 5rem', background: 'white' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <FadeUp style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
              <h3 style={{ fontFamily: '"Barlow Condensed"', fontSize: '1.2rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                {activeCategory === 'All' ? 'Recently Added Parts' : `${activeCategory} Parts`}
              </h3>
              <a href={`https://www.carparts.com/search?q=${encodeURIComponent(activeCategory === 'All' ? 'used parts' : activeCategory)}`}
                target="_blank" rel="noopener noreferrer"
                className="btn-dark" style={{ fontSize: '0.85rem', padding: '0.6rem 1.2rem' }}
              >View All on CarParts.com →</a>
            </div>
          </FadeUp>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.25rem' }}>
            {FEATURED.map((item, i) => (
              <FadeUp key={i} delay={i * 60}>
                <PartCard item={item} />
              </FadeUp>
            ))}
          </div>

          {/* Disclaimer */}
          <FadeUp>
            <div style={{ marginTop: '3rem', background: 'var(--ice)', borderRadius: 12, padding: '1.5rem 2rem', border: '1px solid var(--silver-light)' }}>
              <p style={{ fontSize: '0.88rem', color: 'var(--text-mid)', lineHeight: 1.7 }}>
                <strong>⚠️ Inventory Notice:</strong> The parts shown above are representative examples. Our actual inventory changes daily as new vehicles arrive. For the most accurate availability and pricing, please call us at{' '}
                <a href={`tel:${BUSINESS.phoneRaw}`} style={{ color: 'var(--orange)', textDecoration: 'none', fontWeight: 600 }}>{BUSINESS.phone}</a>{' '}
                or use the search form above to browse live listings. We're happy to confirm availability before your visit.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* How It Works */}
      <section style={{ padding: '5rem 2rem', background: 'var(--navy)' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <FadeUp style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <SectionHeader tag="Simple Process" title="How to Get Your Part" light center />
          </FadeUp>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '2rem', position: 'relative' }}>
            {[
              { step: '01', icon: '🔍', title: 'Search', desc: 'Use our search tool to find parts by vehicle or part name.' },
              { step: '02', icon: '📞', title: 'Call or Request', desc: 'Call us or submit a part request to confirm availability.' },
              { step: '03', icon: '✅', title: 'We Pull It', desc: 'We locate and pull your part from the yard, ready for pickup.' },
              { step: '04', icon: '🚗', title: 'Pick Up', desc: 'Come in during business hours and drive away with your part.' },
            ].map((s, i) => (
              <FadeUp key={s.step} delay={i * 80}>
                <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                  <div style={{ fontFamily: '"Bebas Neue"', fontSize: '3rem', color: 'rgba(232,84,26,0.3)', lineHeight: 1 }}>{s.step}</div>
                  <div style={{ fontSize: '2.5rem', margin: '0.5rem 0' }}>{s.icon}</div>
                  <h4 style={{ fontFamily: '"Barlow Condensed"', fontSize: '1.1rem', fontWeight: 700, color: 'white', letterSpacing: '0.04em', marginBottom: '0.5rem' }}>{s.title}</h4>
                  <p style={{ fontSize: '0.88rem', color: 'var(--silver)', lineHeight: 1.6 }}>{s.desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: 'var(--ice)', padding: '4rem 2rem', textAlign: 'center' }}>
        <FadeUp>
          <h2 style={{ fontFamily: '"Bebas Neue"', fontSize: 'clamp(2rem, 4vw, 3rem)', color: 'var(--navy)', marginBottom: '1rem' }}>
            Can't Find What You Need?
          </h2>
          <p style={{ color: 'var(--text-mid)', marginBottom: '2rem', fontSize: '1.05rem' }}>
            Submit a part request — our locator network covers hundreds of yards across Ontario.
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/request-part" className="btn-primary">📋 Request a Part</Link>
            <a href={`tel:${BUSINESS.phoneRaw}`} className="btn-dark">📞 {BUSINESS.phone}</a>
          </div>
        </FadeUp>
      </section>
    </>
  );
}

function CategoryPill({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      fontFamily: '"Barlow Condensed"', fontSize: '0.85rem', fontWeight: 700,
      letterSpacing: '0.05em', textTransform: 'uppercase',
      padding: '0.5rem 1.1rem', borderRadius: 100, cursor: 'pointer',
      background: active ? 'var(--navy)' : 'var(--ice)',
      color: active ? 'white' : 'var(--text-mid)',
      border: `1.5px solid ${active ? 'var(--navy)' : 'var(--silver-light)'}`,
      transition: 'all 0.2s',
    }}>
      {label}
    </button>
  );
}

function PartCard({ item }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'white', border: `1.5px solid ${hovered ? 'rgba(13,27,42,0.15)' : 'var(--silver-light)'}`,
        borderRadius: 12, padding: '1.5rem', transition: 'all 0.25s',
        transform: hovered ? 'translateY(-3px)' : 'none',
        boxShadow: hovered ? '0 8px 28px rgba(13,27,42,0.1)' : 'none',
        display: 'flex', flexDirection: 'column', gap: '0.75rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '2rem' }}>{item.icon}</div>
        <span style={{
          fontFamily: '"Barlow Condensed"', fontSize: '0.72rem', fontWeight: 700,
          letterSpacing: '0.08em', textTransform: 'uppercase',
          background: 'var(--orange)', color: 'white', padding: '0.2rem 0.6rem', borderRadius: 4,
        }}>{item.tag}</span>
      </div>
      <div>
        <h4 style={{ fontFamily: '"Barlow Condensed"', fontSize: '1.1rem', fontWeight: 700, color: 'var(--navy)', letterSpacing: '0.02em', marginBottom: '0.2rem' }}>{item.part}</h4>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-mid)' }}>{item.vehicle}</p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '0.5rem', borderTop: '1px solid var(--silver-light)' }}>
        <span style={{
          fontSize: '0.78rem', fontWeight: 600, color: CONDITION_COLOR[item.condition] || 'var(--text-mid)',
          background: `${CONDITION_COLOR[item.condition]}18`, padding: '0.25rem 0.6rem', borderRadius: 4,
        }}>✓ {item.condition}</span>
        <span style={{ fontFamily: '"Bebas Neue"', fontSize: '1.4rem', color: 'var(--navy)', letterSpacing: '0.05em' }}>{item.price}</span>
      </div>
      <a href="tel:5197743338" style={{
        display: 'block', textAlign: 'center', background: 'var(--navy)', color: 'white',
        textDecoration: 'none', fontFamily: '"Barlow Condensed"', fontSize: '0.9rem', fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase', padding: '0.65rem', borderRadius: 7,
        transition: 'background 0.2s',
      }}
        onMouseEnter={e => e.target.style.background = 'var(--navy-mid)'}
        onMouseLeave={e => e.target.style.background = 'var(--navy)'}
      >📞 Call to Order</a>
    </div>
  );
}
